import type {
  IBodyAbsoluteCountPagination,
  IRestEndpointLinkedPagination,
  IRestEndpointOffsetPagination,
  IRestEndpointPagination
} from '@deity-io/di-data-flows';
import { Checkbox, FlexLayout, GridLayout, H4, Input, Select } from '@deity-io/portal-ui';
import { SchemaPathPicker } from 'components/FlowEditor/components/panels/FlowComponentSettings/StructurePathPicker';
import { EndpointActionTypes, EndpointContext } from 'contexts/EndpointContext';
import { useContext } from 'react';
import type { FC } from 'react';

// import { GridLayout } from '@deity/falcon-ui';
const parseInt = (value: string): number | undefined => {
  const num = Number.parseInt(value, 10);

  return isNaN(num) ? undefined : num;
};

const isLinkedPagination = (tbd: any): tbd is IRestEndpointLinkedPagination =>
  (tbd as IRestEndpointLinkedPagination)?.type === 'linked';

interface PaginationSettingsProps {
  isEditable: boolean;
  pagination?: Partial<IRestEndpointPagination>;
  parent?: Partial<IRestEndpointPagination>;
  modelData?: any;
}
const PaginationSettings: FC<PaginationSettingsProps> = ({ isEditable, pagination, parent, modelData }) => {
  const ctx = useContext(EndpointContext);

  const onChange = (path: string[], value: any, set = true) => {
    if (ctx) {
      if (set) {
        ctx.dispatch({ type: EndpointActionTypes.SET_VALUE, payload: { key: path, value } });
      } else {
        ctx.dispatch({ type: EndpointActionTypes.UNSET_VALUE, payload: { key: path } });
      }
    }
  };

  const label = !pagination?.type && parent?.type ? `${parent.type.toUpperCase()} (inherited)` : 'Method';
  const paginationType = pagination?.type || parent?.type;
  const useParent = pagination?.type !== undefined;

  return (
    <FlexLayout flexDirection="column" padding="48px">
      <H4 fontWeight="semiBold" fontSize="xl">
        Pagination
      </H4>
      <Select
        label={label}
        sx={{ marginY: 1 }}
        onChange={(val: any) => {
          let newPagination;
          if (val.value === 'none') {
            newPagination = undefined;
          } else if (val.value === 'linked') {
            newPagination = { ...(pagination || {}), type: val.value };
            delete (newPagination as IRestEndpointOffsetPagination).countField;
            delete (newPagination as IRestEndpointOffsetPagination).totalField;
          } else {
            newPagination = { type: val.value };
          }

          onChange(['pagination'], newPagination, newPagination !== undefined);
        }}
        options={['none', 'linked', 'offset', 'bodyOffset', 'bodyAbsolute'].map(m => ({
          value: m,
          label: m.toUpperCase()
        }))}
        placeholder="select method"
        value={pagination?.type ? { label: pagination.type.toUpperCase(), value: pagination.type } : ''}
        disabled={!isEditable}
      />

      {(paginationType === 'offset' || pagination?.type === 'bodyOffset') && (
        <OffsetPagination
          modelData={modelData}
          isEditable={isEditable}
          pagination={pagination as IRestEndpointOffsetPagination}
          parent={useParent ? (parent as Partial<IRestEndpointOffsetPagination>) : undefined}
          onChange={onChange}
        />
      )}
      {paginationType === 'linked' && (
        <LinkedPagination
          modelData={modelData}
          isEditable={isEditable}
          pagination={pagination as IRestEndpointLinkedPagination}
          parent={useParent ? (parent as Partial<IRestEndpointLinkedPagination>) : undefined}
          onChange={onChange}
        />
      )}
      {paginationType === 'bodyAbsolute' && (
        <BodyAbsolutePagination
          modelData={modelData}
          isEditable={isEditable}
          pagination={pagination as IBodyAbsoluteCountPagination}
          parent={useParent ? (parent as Partial<IBodyAbsoluteCountPagination>) : undefined}
          onChange={onChange}
        />
      )}
    </FlexLayout>
  );
};

interface OffsetPaginationProps {
  isEditable: boolean;
  modelData: any;
  pagination?: Partial<IRestEndpointOffsetPagination>;
  parent?: Partial<IRestEndpointOffsetPagination>;
  onChange: (path: string[], val: any) => void;
}
const OffsetPagination: FC<OffsetPaginationProps> = ({ isEditable, pagination, onChange, modelData, parent }) => {
  const offsetParmError = !pagination?.offsetParam?.trim().length && !parent?.offsetParam?.trim().length;
  const limitParmError = !pagination?.limitParam?.trim().length && pagination?.limit !== undefined;

  return (
    <GridLayout container spacing={1}>
      <GridLayout item xs={12}>
        <Input
          label={
            !pagination?.offsetParam && parent?.offsetParam
              ? `${parent.offsetParam} (inherited)`
              : 'Offset parameter name'
          }
          type="text"
          name="Offset name"
          id="variable-path"
          value={pagination?.offsetParam || null}
          placeholder="offset"
          error={offsetParmError}
          onChange={e => onChange(['pagination', 'offsetParam'], e.target.value)}
          disabled={!isEditable}
          sx={{ marginY: 2, flexGrow: 1, paddingRight: '5px' }}
          autoComplete="off"
        />
        <Input
          label={
            !pagination?.initialOffset && parent?.initialOffset
              ? `${parent.initialOffset} (inherited)`
              : 'Initial offset'
          }
          type="number"
          name="Initial offset"
          id="initial-offset"
          value={pagination?.initialOffset || null}
          onChange={e => onChange(['pagination', 'initialOffset'], parseInt(e.target.value))}
          disabled={!isEditable}
          sx={{ marginY: 2, flexGrow: 1, paddingLeft: '5px' }}
          autoComplete="off"
        />
      </GridLayout>
      <GridLayout item xs={12}>
        <Input
          label={
            !pagination?.maxPages && parent?.maxPages ? `${parent.maxPages} (inherited)` : 'Maximum amount of pages'
          }
          type="number"
          name="Maximum pages"
          id="maxPages"
          value={pagination?.maxPages || null}
          onChange={e => onChange(['pagination', 'maxPages'], parseInt(e.target.value))}
          disabled={!isEditable}
          sx={{ marginY: 2, flexGrow: 1, paddingRight: '5px' }}
          autoComplete="off"
        />

        <Checkbox
          label={
            !pagination?.isPageOffset && parent?.isPageOffset
              ? `'Offset is ${parent?.isPageOffset === true ? '' : 'not'} calculated in pages' (inherited)`
              : 'Offset is calculated in pages'
          }
          id="isPageOffset"
          value={pagination?.isPageOffset}
          onChange={e => onChange(['pagination', 'isPageOffset'], e.target.checked)}
          disabled={!isEditable}
          sx={{ marginY: 2, flexGrow: 1, paddingLeft: '5px' }}
        />
      </GridLayout>
      <GridLayout item xs={12}>
        <Input
          label={
            !pagination?.limitParam && parent?.limitParam ? `${parent.limitParam} (inherited)` : 'Limit parameter name'
          }
          type="text"
          name="Offset name"
          id="limit-path"
          error={limitParmError}
          value={pagination?.limitParam || null}
          placeholder="limit"
          onChange={e => onChange(['pagination', 'limitParam'], e.target.value)}
          disabled={!isEditable}
          sx={{ marginY: 2, flexGrow: 1, paddingRight: '5px' }}
          autoComplete="off"
        />
        <Input
          label={!pagination?.limit && parent?.limit ? `${parent.limitParam} (inherited)` : 'Limit'}
          type="number"
          name="Limit"
          id="limit"
          value={pagination?.limit || null}
          onChange={e => onChange(['pagination', 'limit'], parseInt(e.target.value))}
          disabled={!isEditable}
          sx={{ marginY: 2, flexGrow: 1, paddingLeft: '5px' }}
          autoComplete="off"
        />
      </GridLayout>
      <GridLayout item xs={12}>
        <SchemaPathPicker
          property="link"
          inputLabel={
            !pagination?.countField && parent?.countField ? `${parent.countField} (inherited)` : 'Count field name'
          }
          placeholder="/meta/count"
          value={pagination?.countField}
          schema={modelData}
          isEditable={isEditable}
          onChange={(key, value) => onChange(['pagination', 'countField'], value)}
        />

        <SchemaPathPicker
          property="link"
          inputLabel={
            !pagination?.totalField && parent?.totalField ? `${parent.totalField} (inherited)` : 'Total field name'
          }
          placeholder="/meta/total"
          value={pagination?.totalField}
          schema={modelData}
          isEditable={isEditable}
          onChange={(key, value) => onChange(['pagination', 'totalField'], value)}
        />
      </GridLayout>
    </GridLayout>
  );
};

interface LinkedPaginationProps {
  isEditable: boolean;
  modelData: any;
  pagination?: Partial<IRestEndpointLinkedPagination>;
  parent?: Partial<IRestEndpointLinkedPagination>;
  onChange: (path: string[], val: any) => void;
}
const LinkedPagination: FC<LinkedPaginationProps> = ({ isEditable, pagination, onChange, modelData, parent }) => {
  const offsetParmError = !pagination?.offsetParam?.trim().length && pagination?.initialOffset !== undefined;
  const limitParmError = !pagination?.limitParam?.trim().length && pagination?.limit !== undefined;

  return (
    <FlexLayout flexDirection="column" width="full">
      <SchemaPathPicker
        property="link"
        inputLabel={!pagination?.link && parent?.link ? `${parent.link} (inherited)` : 'Link field'}
        placeholder="/meta/next"
        value={pagination?.link}
        schema={modelData}
        onChange={(key, value) => onChange(['pagination', 'link'], value)}
      />
      <GridLayout container>
        <GridLayout item>
          <Input
            label={
              !pagination?.offsetParam && parent?.offsetParam
                ? `${parent.offsetParam} (inherited)`
                : 'Offset parameter name'
            }
            type="text"
            name="Offset name"
            id="variable-path"
            error={offsetParmError}
            value={pagination?.offsetParam || null}
            placeholder="offset"
            onChange={e => onChange(['pagination', 'offsetParam'], e.target.value)}
            disabled={!isEditable}
            sx={{ marginY: 2, flexGrow: 1, paddingRight: '5px' }}
            autoComplete="off"
          />
          <Input
            label={
              !pagination?.initialOffset && parent?.initialOffset
                ? `${parent.initialOffset} (inherited)`
                : 'Initial offset'
            }
            type="number"
            name="Initial offset"
            id="initial-offset"
            value={pagination?.initialOffset || null}
            onChange={e => onChange(['pagination', 'initialOffset'], parseInt(e.target.value))}
            disabled={!isEditable}
            sx={{ marginY: 2, flexGrow: 1, paddingLeft: '5px' }}
            autoComplete="off"
          />
        </GridLayout>
        <GridLayout item>
          <Input
            label={
              !pagination?.maxPages && parent?.maxPages ? `${parent.maxPages} (inherited)` : 'Maximum amount of pages'
            }
            type="number"
            name="Maximum pages"
            id="maxPages"
            value={pagination?.maxPages || null}
            onChange={e => onChange(['pagination', 'maxPages'], parseInt(e.target.value))}
            disabled={!isEditable}
            sx={{ marginY: 2, flexGrow: 1 }}
            autoComplete="off"
          />
        </GridLayout>
        <GridLayout item>
          <Input
            label={
              !pagination?.limitParam && parent?.limitParam
                ? `${parent.limitParam} (inherited)`
                : 'Limit parameter name'
            }
            type="text"
            name="Offset name"
            id="limit-path"
            error={limitParmError}
            value={pagination?.limitParam || null}
            placeholder="limit"
            onChange={e => onChange(['pagination', 'limitParam'], e.target.value)}
            disabled={!isEditable}
            sx={{ marginY: 2, flexGrow: 1, paddingRight: '5px' }}
            autoComplete="off"
          />
          <Input
            label={!pagination?.limit && parent?.limit ? `${parent.limit} (inherited)` : 'Limit'}
            type="number"
            name="Limit"
            id="limit"
            value={pagination?.limit || null}
            onChange={e => onChange(['pagination', 'limit'], parseInt(e.target.value))}
            disabled={!isEditable}
            sx={{ marginY: 2, flexGrow: 1, paddingLeft: '5px' }}
            autoComplete="off"
          />
        </GridLayout>
      </GridLayout>
    </FlexLayout>
  );
};

interface BodyAbsolutePaginationProps {
  isEditable: boolean;
  modelData: any;
  pagination?: Partial<IBodyAbsoluteCountPagination>;
  parent?: Partial<IBodyAbsoluteCountPagination>;
  onChange: (path: string[], val: any) => void;
}
const BodyAbsolutePagination: FC<BodyAbsolutePaginationProps> = ({
  isEditable,
  pagination,
  onChange,
  modelData,
  parent
}) => {
  const startParmError = !pagination?.startParam?.trim().length && pagination?.startParam !== undefined;
  const endParmError = !pagination?.endParam?.trim().length && pagination?.endParam !== undefined;
  const limitError = pagination?.limit === undefined || pagination?.limit < 1;

  return (
    <FlexLayout flexDirection="column" width="full">
      <GridLayout container>
        <GridLayout item>
          <SchemaPathPicker
            property="startParam"
            inputLabel={
              !pagination?.startParam && parent?.startParam ? `${parent.startParam} (inherited)` : 'Start count field'
            }
            placeholder="changeNumberStart"
            value={pagination?.startParam}
            error={startParmError}
            schema={modelData}
            onChange={(key, value) => onChange(['pagination', 'startParam'], value)}
          />
        </GridLayout>
        <GridLayout item>
          <SchemaPathPicker
            property="endParam"
            inputLabel={
              !pagination?.endParam && parent?.endParam ? `${parent.endParam} (inherited)` : 'End count field'
            }
            placeholder="changeNumberEnd"
            value={pagination?.endParam}
            error={endParmError}
            schema={modelData}
            onChange={(key, value) => onChange(['pagination', 'endParam'], value)}
          />
        </GridLayout>
        <GridLayout item>
          <Input
            label={!pagination?.limit && parent?.limit ? `${parent.limit} (inherited)` : 'Limit'}
            type="number"
            name="Limit"
            id="limit"
            error={limitError}
            value={pagination?.limit || ''}
            onChange={e => onChange(['pagination', 'limit'], parseInt(e.target.value))}
            disabled={!isEditable}
            sx={{ marginY: 2, flexGrow: 1, paddingLeft: '5px' }}
            autoComplete="off"
          />
        </GridLayout>
      </GridLayout>
    </FlexLayout>
  );
};

export default PaginationSettings;
